import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Search, MoreHorizontal, Eye, Trash2, UserPlus, Star, Sparkles, Award, AlertTriangle, Send } from "lucide-react";
import type { Applicant, ApplicationStage } from "@/types/applicant";
import { stageLabels, stageColors, stageOrder } from "@/types/applicant";

interface InvitationInfo {
  applicant_id: string;
  sent_at: string;
  response_status: string;
}
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ApplicantListProps {
  applicants: Applicant[];
  onUpdate: () => void;
}

const ApplicantList = ({ applicants, onUpdate }: ApplicantListProps) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState<ApplicationStage | "all">("all");
  const [deleteApplicant, setDeleteApplicant] = useState<Applicant | null>(null);
  const [invitations, setInvitations] = useState<Record<string, InvitationInfo>>({});

  useEffect(() => {
    loadInvitations();
  }, [applicants]);

  const loadInvitations = async () => {
    if (applicants.length === 0) return;
    
    const { data } = await supabase
      .from("applicant_interview_invitations")
      .select("applicant_id, sent_at, response_status")
      .in("applicant_id", applicants.map(a => a.id))
      .order("sent_at", { ascending: false });

    if (data) {
      const invMap: Record<string, InvitationInfo> = {};
      data.forEach(inv => {
        if (!invMap[inv.applicant_id]) {
          invMap[inv.applicant_id] = inv;
        }
      });
      setInvitations(invMap);
    }
  };

  // Prüfen ob Bewerber eine Nachverfolgung braucht (7 Tage ohne Aktivität)
  const needsFollowUp = (applicant: Applicant): { needs: boolean; daysSinceLastAction: number } => {
    const now = new Date();
    const updatedAt = new Date(applicant.updated_at);
    const daysSinceUpdate = Math.floor((now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60 * 24));
    
    const invitation = invitations[applicant.id];
    
    if (invitation && invitation.response_status === 'pending') {
      const invitationDate = new Date(invitation.sent_at);
      const daysSinceInvitation = Math.floor((now.getTime() - invitationDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysSinceInvitation >= 7) {
        return { needs: true, daysSinceLastAction: daysSinceInvitation };
      }
    }
    
    if (daysSinceUpdate >= 7) {
      return { needs: true, daysSinceLastAction: daysSinceUpdate };
    }
    
    return { needs: false, daysSinceLastAction: daysSinceUpdate };
  };

  const filteredApplicants = applicants.filter((applicant) => {
    const matchesSearch =
      `${applicant.first_name} ${applicant.last_name} ${applicant.email}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesStage =
      stageFilter === "all" || applicant.current_stage === stageFilter;
    return matchesSearch && matchesStage;
  });

  const handleDelete = async () => {
    if (!deleteApplicant) return;

    try {
      const { error } = await supabase
        .from("applicants")
        .delete()
        .eq("id", deleteApplicant.id);

      if (error) throw error;
      toast.success("Bewerber gelöscht");
      onUpdate();
    } catch (error) {
      console.error("Error deleting applicant:", error);
      toast.error("Fehler beim Löschen des Bewerbers");
    } finally {
      setDeleteApplicant(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Suchen nach Name oder E-Mail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={stageFilter}
          onValueChange={(v) => setStageFilter(v as ApplicationStage | "all")}
        >
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Alle Phasen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Phasen</SelectItem>
            {stageOrder.map((stage) => (
              <SelectItem key={stage} value={stage}>
                {stageLabels[stage]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="hidden sm:table-cell">E-Mail</TableHead>
              <TableHead className="hidden md:table-cell">Telefon</TableHead>
              <TableHead>Phase</TableHead>
              <TableHead className="hidden lg:table-cell">Bewertung</TableHead>
              <TableHead className="hidden md:table-cell">Eingang</TableHead>
              <TableHead className="hidden lg:table-cell">Letzte E-Mail</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredApplicants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  Keine Bewerber gefunden
                </TableCell>
              </TableRow>
            ) : (
              filteredApplicants.map((applicant) => {
                const followUpStatus = needsFollowUp(applicant);
                const invitation = invitations[applicant.id];
                
                return (
                <TableRow
                  key={applicant.id}
                  className={`cursor-pointer hover:bg-muted/50 ${followUpStatus.needs ? 'bg-amber-50/50 dark:bg-amber-950/20' : ''}`}
                  onClick={() => navigate(`/applicants/${applicant.id}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {applicant.first_name} {applicant.last_name}
                          {followUpStatus.needs && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">
                                    {followUpStatus.daysSinceLastAction} Tage ohne Aktivität
                                    <br />
                                    <span className="text-muted-foreground">Bitte nachfassen!</span>
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </p>
                        {applicant.desired_position && (
                          <p className="text-xs text-muted-foreground">
                            {applicant.desired_position}
                          </p>
                        )}
                      </div>
                      {applicant.career_summary && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 shrink-0 cursor-help">
                                <Sparkles className="h-3.5 w-3.5 text-primary" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs p-3" sideOffset={5}>
                              <div className="space-y-1">
                                <p className="font-semibold text-xs mb-2 flex items-center gap-1">
                                  <Award className="h-3 w-3" />
                                  Karriere-Highlights
                                </p>
                                {applicant.career_summary.split('\n').filter(Boolean).map((point, idx) => (
                                  <p key={idx} className="text-xs">
                                    {point.replace(/^[•\-]\s*/, '• ')}
                                  </p>
                                ))}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    {applicant.email}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {applicant.phone}
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`${stageColors[applicant.current_stage]} text-white`}
                    >
                      {stageLabels[applicant.current_stage]}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {applicant.rating ? (
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-3 w-3 ${
                              i < applicant.rating!
                                ? "fill-amber-400 text-amber-400"
                                : "text-gray-300"
                            }`}
                          />
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {new Date(applicant.created_at).toLocaleDateString("de-DE")}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {invitation ? (
                      <div className="flex items-center gap-1 text-xs">
                        <Send className="h-3 w-3 text-primary" />
                        <span>{new Date(invitation.sent_at).toLocaleDateString("de-DE")}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/applicants/${applicant.id}`);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Details
                        </DropdownMenuItem>
                        {applicant.current_stage === "offer" && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/applicants/${applicant.id}/convert`);
                            }}
                          >
                            <UserPlus className="h-4 w-4 mr-2" />
                            Zu Mitarbeiter
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteApplicant(applicant);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteApplicant} onOpenChange={() => setDeleteApplicant(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bewerber löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie {deleteApplicant?.first_name} {deleteApplicant?.last_name} wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ApplicantList;
